import { z } from 'zod';

import { AddPreflightMarketSchema, marketEntryKey } from './market.js';

/**
 * Catalogue key `{ kind, code }` accepted on create-report / scoring-job bodies.
 * Strict so an extra field such as `marketId` is a 400 rather than silently
 * dropped while a different market gets scored.
 */
export const PreflightMarketCatalogueKeySchema = AddPreflightMarketSchema.strict();
export type PreflightMarketCatalogueKey = z.infer<typeof PreflightMarketCatalogueKeySchema>;

export const CreateReportMarketsSchema = z
  .array(PreflightMarketCatalogueKeySchema)
  .min(1, 'At least one market is required')
  .refine(
    (markets) => new Set(markets.map(marketEntryKey)).size === markets.length,
    'Duplicate market entries are not allowed',
  );

const hasNonEmptyMarketIds = (marketIds: string[] | undefined): boolean =>
  (marketIds?.length ?? 0) > 0;

const hasNonEmptyMarkets = (
  markets: PreflightMarketCatalogueKey[] | undefined,
): boolean => (markets?.length ?? 0) > 0;

/** Exactly one of `marketIds` or `markets` must be present on create-report bodies. */
export function assertExactlyOneMarketSelector(
  body: { marketIds?: string[]; markets?: PreflightMarketCatalogueKey[] },
  ctx: z.RefinementCtx,
): void {
  const usingIds = hasNonEmptyMarketIds(body.marketIds);
  const usingKeys = hasNonEmptyMarkets(body.markets);
  if (usingIds === usingKeys) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Exactly one of marketIds or markets is required',
      path: usingIds && usingKeys ? ['marketIds'] : ['markets'],
    });
  }
}
